import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { TOAST_TYPES, showToast } from 'components/ToastUtils';
import { AddressDTO, CartCap, ClientDTO, MarcaDTO } from 'service/apicataleg/types/ApiCatalegTypes';

export type ClientSliceState = {
  loading: boolean;
  client: ClientDTO | null;
  carts: Array<CartCap>;
  addresses: Array<AddressDTO>;
  marques: Array<MarcaDTO>;
  selectedcart: CartCap | null;
  id: number;
};

type SetClientParams = {
  client: ClientDTO;
  carts: Array<CartCap>;
  addresses: Array<AddressDTO>;
  marques: Array<MarcaDTO>;
};

const initialState: ClientSliceState = {
  loading: false,
  client: null,
  carts: [],
  marques: [],
  selectedcart: null,
  addresses: [],
  id: 0,
};

export const ClientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setClient: (state, action: PayloadAction<SetClientParams>) => {
      const { selectedcart } = state;
      const { payload } = action;
      const newCarts = payload.carts;
      let newSelected = payload.carts.find((x) => x.id === selectedcart?.id) || null;

      if (!newSelected && newCarts.length > 0) {
        newSelected = newCarts[0];
      }
      return { ...payload, loading: false, selectedcart: newSelected, id: payload.client.codi };
    },

    setCarts: (state, action: PayloadAction<Array<CartCap>>) => {
      const { payload } = action;
      const { selectedcart, carts, ...rest } = state;

      const updateSelected = payload.find((x) => x.id === selectedcart?.id) || null;
      if (selectedcart && updateSelected === undefined) {
        showToast(TOAST_TYPES.ERROR, 'Cart not found');
      }

      const newCarts = payload || [];
      const newState = { ...rest, carts: newCarts, selectedcart: updateSelected };
      return newState;
    },

    clearClient: (state) => {
      return initialState;
    },

    setAddresses: (state, action: PayloadAction<Array<AddressDTO>>) => {
      const { payload } = action;
      const { addresses, ...rest } = state;
      return { ...rest, addresses: payload };
    },

    addAddress: (state, { payload }: PayloadAction<AddressDTO>) => {
      const { addresses, ...rest } = state;
      return { ...rest, addresses: [...addresses, payload] };
    },

    updateAddress: (state, { payload }: PayloadAction<AddressDTO>) => {
      const { addresses, ...rest } = state;
      const addrs = addresses.filter((x) => x.codi !== payload.codi);
      return { ...rest, addresses: [...addrs, payload] };
    },

    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      const loading = payload;
      return { ...state, loading };
    },

    addCart: (state, { payload }: PayloadAction<CartCap>) => {
      const { carts, ...rest } = state;
      const currentCarts = [...carts, payload];
      return { ...rest, carts: currentCarts, selectedcart: payload };
    },

    removeCart: (state, { payload }: PayloadAction<number>) => {
      const { selectedcart } = state;
      const selcart = selectedcart && selectedcart.id === payload ? null : selectedcart;
      return {
        ...state,
        selectedcart: selcart,
        carts: state.carts.filter((x) => x.id !== payload),
      };
    },

    selectCart: (state, { payload }: PayloadAction<number>) => {
      const selectedcart = state.carts.find((x) => x.id === payload);

      if (selectedcart) return { ...state, selectedcart };

      showToast(TOAST_TYPES.ERROR, 'Cart not found');
      return { ...state, selectedcart: null };
    },
  },
});

export const {
  setClient,
  clearClient,
  selectCart,
  setCarts,
  setLoading,
  setAddresses,
  addAddress,
  updateAddress,
  addCart,
  removeCart,
} = ClientSlice.actions;
export default ClientSlice.reducer;
